import { SectionTitle, SWRDataTable, toast } from "@bleu.builders/ui";
import * as React from "react";
import {
  json,
  redirect,
  redirectDocument,
  useLoaderData,
  useParams,
} from "react-router-dom";

import { detailsPageBuilder } from "#/components/details_page_builder/builder";
import { Link } from "#/components/Link";
import { SettingsCard } from "#/components/SettingsCard";
import { withPaywall } from "#/components/WithPaywall";
import { useAdminPreview } from "#/contexts/AdminPreviewContext";
import { AddResourceButton } from "#/pages/(components)/AddResourceModal";
import { FieldsLoaderData } from "#/pages/(components)/types";
import { client } from "#/utils/api-client";

export enum UserPagePath {
  DASHBOARD = "/dashboard",
  ORGANIZATION_PROFILE_SETTINGS = "/settings/me",
  ORGANIZATION_BILLING_SETTINGS = "/settings/billing",
  ORGANIZATION_TRANSACTIONS = "/settings/transactions",
  ORGANIZATION_BRAND_THEME_SETTINGS = "/settings/brand_theme",
  ORGANIZATION_SETTINGS = "/settings/organization",
  ORGANIZATION_INVOICES = "/organizations/:organization_id/invoices",
  NEWS_ARTICLES = "/news_articles",
  NEWS_ARTICLES_LATEST = "/news_articles/latest",
  NEWS_ARTICLE = "/news_articles/:news_article_id",
  THESES = "/theses",
  THESES_PER_BUSINESS_ACTIVITY = "/theses/business_activity",
  TRENDING_THESES = "/theses/trending",
  LATEST_THESIS = "/theses/latest",
  PURCHASE_THESIS = "/theses/:thesis_id/purchase",
  PURCHASED_THESES = "/theses/purchased",
  PETITIONS = "/petition_models",
  PETITION = "/petition_models/:petition_model_id",
  DIAGNOSES = "/diagnoses",
  DIAGNOSES_EXTERNAL = "/diagnoses/multi-external/:reusable_diagnosis_link_code",
  DIAGNOSES_UPDATE = "/diagnoses/:diagnosis_id",
  DIAGNOSES_NEW = "/diagnoses/new",
  DIAGNOSES_NEW_EXTERNAL = "/diagnoses/multi-external/new/:reusable_diagnosis_link_code",
  DIAGNOSES_EDIT = "/diagnoses/:diagnosis_id/edit",
  DIAGNOSES_EXTERNAL_NEW = "/diagnoses/external/new",
  DIAGNOSES_MATCHES = "/diagnoses/:diagnosis_id/matches",
  ORGANIZATION_UPDATE = "/organization",
  ORGANIZATION_EDIT = "/organization/edit",
  ORGANIZATION_GATEWAY_CHECKOUT = "/organization/gateway_checkout",
  SETTINGS_USER_UPDATE = "/users/me",
  SETTINGS_USER_EDIT = "/users/me/edit",
  SETTINGS_USER_CREDITS = "/users/me/transactions",
  ORGANIZATION_DESIGN_SETTINGS_UPDATE = "/brand_theme",
  ORGANIZATION_DESIGN_SETTINGS_EDIT = "/brand_theme/edit",
  USER_ONBOARDING = "/users/:user_id/onboarding",
  USER_ONBOARDING_NEW = "/users/:user_id/onboarding/new",
  EVENT_DETAILS = "/events/:event_id",
}

export enum AdminPagePath {
  THESES = "/admin/theses",
  THESES_UPDATE = "/admin/theses/:thesis_id",
  THESES_NEW = "/admin/theses/new",
  THESES_EDIT = "/admin/theses/:thesis_id/edit",
  THESIS_UPDATES = "/admin/theses/:thesis_id/thesis_updates",
  THESIS_UPDATES_NEW = "/admin/theses/:thesis_id/thesis_updates/new",
  THESIS_UPDATES_EDIT = "/admin/theses/:thesis_id/thesis_updates/:thesis_update_id/edit",
  THESIS_UPDATES_UPDATE = "/admin/theses/:thesis_id/thesis_updates/:thesis_update_id",
  PETITION_MODELS = "/admin/petition_models",
  PETITION_MODELS_UPDATE = "/admin/petition_models/:petition_model_id",
  PETITION_MODELS_NEW = "/admin/petition_models/new",
  PETITION_MODELS_EDIT = "/admin/petition_models/:petition_model_id/edit",
  NEWS_ARTICLES = "/admin/news_articles",
  NEWS_ARTICLES_UPDATE = "/admin/news_articles/:news_article_id",
  NEWS_ARTICLES_NEW = "/admin/news_articles/new",
  NEWS_ARTICLES_EDIT = "/admin/news_articles/:news_article_id/edit",
  USERS = "/admin/users",
  USERS_NEW = "/admin/users/new",
  ORGANIZATIONS = "/admin/organizations",
  ORGANIZATION_DETAILS = "/admin/organizations/:organization_id",
  ORGANIZATION_CHARGES = "/admin/organizations/:organization_id/pay/charges",
  ORGANIZATION_INVOICES = "/admin/organizations/:organization_id/pay/invoices",
  ORGANIZATION_USERS = "/admin/organizations/:organization_id/users",
  ORGANIZATION_TRANSACTIONS = "/admin/organizations/:organization_id/transactions",
  SUBSCRIPTIONS = "/admin/pay/subscriptions",
  PAYMENT_METHODS = "/admin/pay/payment_methods",
  CHARGES = "/admin/pay/charges",
  BILLING_COUPONS = "/admin/billing_coupons",
  BILLING_COUPONS_UPDATE = "/admin/billing_coupons/:billing_coupon_id",
  BILLING_COUPONS_NEW = "/admin/billing_coupons/new",
  BILLING_COUPONS_EDIT = "/admin/billing_coupons/:billing_coupon_id/edit",
  BILLING_PLANS = "/admin/billing_plans",
  BILLABLE_BILLING_PLANS = "/admin/billing_plans/billable",
  BILLING_PLANS_UPDATE = "/admin/billing_plans/:billing_plan_id",
  BILLING_PLANS_NEW = "/admin/billing_plans/new",
  BILLING_PLANS_EDIT = "/admin/billing_plans/:billing_plan_id/edit",
  IMPERSONATE = "/admin/users/:user_id/impersonate",
  STOP_IMPERSONATING = "/admin/users/stop_impersonating",
  CHECKOUT_LINKS = "/admin/checkout_links",
  EVENTS = "/admin/events",
  EVENTS_NEW = "/admin/events/new",
  EVENTS_EDIT = "/admin/events/:event_id/edit",
  EVENTS_UPDATE = "/admin/events/:event_id",
}

export function replaceParams(path: string, params: Record<string, string>) {
  return path.replace(/:\w+/g, (key) => {
    const keyName = key.replace(":", "");
    const id = params[keyName];
    if (!id) {
      throw new Error(`Missing param ${keyName}`);
    }
    return id;
  });
}

export const generateLoader = (path: string) => {
  return async ({ params }) => {
    const normalizedPath = replaceParams(path, {
      ...params,
    });

    const { data, preview, ...rest } = await client(normalizedPath, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    return json({ data, preview, ...rest });
  };
};

type RedirectType = true | false | ((data: unknown) => string);

export const generateAction =
  (
    path: string,
    modelName: string,
    mode: "create" | "update",
    shouldRedirect: RedirectType = true,
  ) =>
  async ({ params, request }) => {
    const formData = await request.formData();
    const normalizedPath = replaceParams(path, params);

    const method = mode === "create" ? "POST" : "PUT";
    const data = await client(normalizedPath, { body: formData, method });

    toast({
      title: data?.success ? "Sucesso!" : "Erro!",
      description: data?.success
        ? `${modelName} alterado com sucesso.`
        : data?.errors,
      variant: data?.success ? "default" : "destructive",
    });

    if (data?.success && shouldRedirect === true) {
      const to =
        mode === "create" ? path : path.split("/").slice(0, -1).join("/");

      return redirect(replaceParams(to, params));
    }

    if (data?.success && typeof shouldRedirect === "function") {
      const to = replaceParams(shouldRedirect(data), params);
      if (to === location.pathname) return redirectDocument(to);

      return redirect(to);
    }

    return json({ data });
  };

interface BasePageProps {
  modelName: string;
  actionPath: string;
  title: string;
}

function BasePage({ modelName, actionPath, title }: BasePageProps) {
  const { data } = useLoaderData() as FieldsLoaderData;

  return (
    <div className="my-8 space-y-4">
      <SettingsCard
        defaultValues={data.defaultValues}
        fields={data.fields}
        title={title}
        action={{ path: actionPath }}
      />
    </div>
  );
}

function NewPage({ modelName, actionPath }: Omit<BasePageProps, "title">) {
  return (
    <BasePage
      modelName={modelName}
      actionPath={actionPath}
      title={`Criar ${modelName}`}
    />
  );
}

function EditPage({ modelName, actionPath }: Omit<BasePageProps, "title">) {
  const params = useParams();

  const path = replaceParams(actionPath, params);

  return (
    <BasePage
      modelName={modelName}
      actionPath={path}
      title={`Atualizar ${modelName}`}
    />
  );
}

export const ShowPage = ({
  children,
  modelName,
}: {
  children?: React.ReactNode;
  modelName: string;
}) => {
  // @ts-ignore-next-line
  const { data, preview } = useLoaderData();

  const { setPreview } = useAdminPreview();

  React.useEffect(() => {
    setPreview(preview);

    return () => {
      setPreview(null);
    };
  }, [preview]);

  return (
    <div className="max-w-7xl w-full mx-auto bg-background">
      <h2 className="py-4 text-2xl font-bold text-foreground/80 ml">
        {modelName} - Detalhes
      </h2>
      <div className="mb-12 p-4 rounded-md shadow-lg border border-primary border-dashed">
        <div className="flex w-full">
          <div className="w-2/3 flex flex-col gap-2 space-y-4 rounded-md p-5 bg-background text-sm font-light">
            {detailsPageBuilder(data)}
          </div>
          <div className="w-1/3">{children}</div>
        </div>
      </div>
    </div>
  );
};

export const ADMIN_PAGES = [
  {
    title: "Teses",
    href: AdminPagePath.THESES,
  },
  {
    title: "Modelos de petições",
    href: AdminPagePath.PETITION_MODELS,
  },
  {
    title: "Monitoramento Tributário",
    href: AdminPagePath.NEWS_ARTICLES,
  },
  {
    title: "Usuários",
    href: AdminPagePath.USERS,
  },
  {
    title: "Organizações",
    href: AdminPagePath.ORGANIZATIONS,
  },
  {
    title: "Planos",
    href: AdminPagePath.BILLING_PLANS,
  },
  {
    title: "Cupons",
    href: AdminPagePath.BILLING_COUPONS,
  },
  {
    title: "Links de Checkout",
    href: AdminPagePath.CHECKOUT_LINKS,
  },
  {
    title: "Eventos de Notificação",
    href: AdminPagePath.EVENTS,
  },
];

export const END_USER_PAGES = [
  {
    title: "Perfil",
    href: UserPagePath.ORGANIZATION_PROFILE_SETTINGS,
  },
  {
    title: "Configurações da Organização",
    href: UserPagePath.ORGANIZATION_SETTINGS,
  },
  {
    title: "Sua Assinatura",
    href: UserPagePath.ORGANIZATION_BILLING_SETTINGS,
  },
  {
    title: "Histórico de Créditos",
    href: UserPagePath.ORGANIZATION_TRANSACTIONS,
  },
  {
    title: "Configurações da Marca",
    href: UserPagePath.ORGANIZATION_BRAND_THEME_SETTINGS,
  },
];

export const Dashboard = ({ fetchPath, action, searchKey }) => {
  const params = useParams();
  const normalizedPath = replaceParams(fetchPath, params);

  return (
    <SWRDataTable
      fetchPath={normalizedPath}
      action={action}
      searchKey={searchKey}
    />
  );
};

export const IndexPage = ({ modelName, fetchPath, action, searchKey }) => {
  return (
    <div>
      <div className="mb-10">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <SectionTitle>
              <span className="font-title">{modelName}</span>
            </SectionTitle>
            <p className="text-muted-foreground">
              Aqui está uma lista de {modelName.toLocaleLowerCase()}.
            </p>
          </div>
        </div>
        <Dashboard
          fetchPath={fetchPath}
          action={action}
          searchKey={searchKey}
        />
      </div>
    </div>
  );
};

const IndexPageActionButton = ({ link, text, setParams }) => {
  const params = useParams();

  return (
    <Link to={setParams ? replaceParams(link, params) : link}>
      <AddResourceButton text={text} />
    </Link>
  );
};

export const buildIndexPageActionButton = ({
  link,
  text,
  setParams = false,
}) => {
  return (
    <IndexPageActionButton link={link} text={text} setParams={setParams} />
  );
};

export const buildShowPage = ({
  modelName,
  loaderPath,
  predicate = () => true,
  Component = ShowPage,
}: {
  modelName: string;
  loaderPath: string;
  predicate?: (user) => boolean;
  Component?: React.ComponentType<{ modelName: string }>;
}) => {
  const WithPaywallShowPage = withPaywall(Component, predicate);

  return {
    loader: generateLoader(loaderPath),
    element: <WithPaywallShowPage modelName={modelName} />,
  };
};

export const buildIndexPage = ({
  modelName,
  fetchPath,
  action,
  searchKey,
  predicate = () => true,
  Component = IndexPage,
}: {
  modelName: string;
  fetchPath: string;
  action: unknown;
  searchKey: string;
  predicate?: (user) => boolean;
  Component?: React.ComponentType<{
    modelName: string;
    fetchPath: string;
    action: unknown;
    searchKey: string;
  }>;
}) => {
  const WithPaywallIndexPage = withPaywall(Component, predicate);

  return {
    element: (
      <WithPaywallIndexPage
        modelName={modelName}
        fetchPath={fetchPath}
        action={action}
        searchKey={searchKey}
      />
    ),
  };
};

export const buildNewPage = ({
  modelName,
  actionPath,
  loaderPath,
  predicate = () => true,
  Component = NewPage,
  redirectAfterAction = true,
}: {
  modelName: string;
  actionPath: string;
  loaderPath: string;
  predicate?: (user) => boolean;
  Component?: React.ComponentType<BasePageProps>;
  redirectAfterAction?: RedirectType;
}) => {
  const WithPaywallNewPage = withPaywall(Component, predicate);

  return {
    loader: generateLoader(loaderPath),
    action: generateAction(
      actionPath,
      modelName,
      "create",
      redirectAfterAction,
    ),
    element: (
      <WithPaywallNewPage modelName={modelName} actionPath={actionPath} />
    ),
  };
};

export const buildEditPage = ({
  modelName,
  actionPath,
  loaderPath,
  predicate = () => true,
  Component = EditPage,
  redirectAfterAction = true,
}: {
  modelName: string;
  actionPath: string;
  loaderPath: string;
  predicate?: (user) => boolean;
  Component?: React.ComponentType<BasePageProps>;
  redirectAfterAction?: RedirectType;
}) => {
  const WithPaywallEditPage = withPaywall(Component, predicate);

  return {
    loader: generateLoader(loaderPath),
    action: generateAction(
      actionPath,
      modelName,
      "update",
      redirectAfterAction,
    ),
    element: (
      <WithPaywallEditPage modelName={modelName} actionPath={actionPath} />
    ),
  };
};
